import { useCallback, useMemo, useState } from 'react';
import type { Conversation, Participant } from '../types';
import { getParticipantName } from '../utils';

/**
 * Manages chat @mention detection, filtering, selection, and text insertion.
 */
export function useChatMentions({
    conversation,
    onSelectMention,
}: {
    conversation?: Conversation;
    onSelectMention: (participant: Participant) => void;
}) {
    const [mentionContext, setMentionContext] = useState<{
        query: string;
    } | null>(null);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const participants = useMemo<Participant[]>(() => {
        return conversation?.Participants ?? [];
    }, [conversation]);
    const filteredParticipants = useMemo(() => {
        if (!mentionContext) {
            return [];
        }
        const normalizedQuery = mentionContext.query.trim().toLocaleLowerCase();
        return participants.filter((participant) => {
            const participantName = getParticipantName(participant);
            if (!normalizedQuery) {
                return true;
            }
            return participantName
                .toLocaleLowerCase()
                .includes(normalizedQuery);
        });
    }, [mentionContext, participants]);

    const resetMentions = useCallback(() => {
        setMentionContext(null);
        setSelectedMentionIndex(0);
    }, []);

    const updateMentionContext = useCallback(
        (value: string, caretIndex: number | null) => {
            const nextMentionContext = getMentionContext({
                value,
                caretIndex,
            });
            setMentionContext(nextMentionContext);
            setSelectedMentionIndex(0);
        },
        [],
    );

    const selectMention = useCallback(
        (selectedParticipant: Participant) => {
            if (!mentionContext) {
                return;
            }
            onSelectMention(selectedParticipant);
            resetMentions();
        },
        [mentionContext, onSelectMention, resetMentions],
    );

    return {
        /**
         * The participants that match the current mention context.
         */
        mentionParticipants: filteredParticipants,
        /**
         * The index of the currently selected mention.
         */
        selectedMentionIndex,
        /**
         * Set the index of the currently selected mention.
         */
        setSelectedMentionIndex,
        /**
         * Update the mention context. Call this when the input or cursor position changes.
         */
        updateMentionContext,
        /**
         * Select a participant to mention.
         */
        selectMention,
        /**
         * Reset the mention context. Call this when you want to hide the mention dropdown.
         */
        resetMentions,
    };
}

/**
 * Find the active @mention token around the current cursor position.
 */
export function getMentionContext({
    value,
    caretIndex,
}: {
    value: string;
    caretIndex: number | null;
}): { startIndex: number; query: string } | null {
    if (caretIndex === null || caretIndex < 0) {
        return null;
    }
    for (let index = caretIndex - 1; index >= 0; index--) {
        if (value[index] !== '@') {
            continue;
        }
        const previousCharacter = index > 0 ? (value.at(index - 1) ?? '') : '';
        // Mentions only trigger at start-of-text or after a space/newline.
        if (
            previousCharacter !== '' &&
            previousCharacter !== ' ' &&
            previousCharacter !== '\n'
        ) {
            continue;
        }
        const query = value.slice(index + 1, caretIndex);
        if (
            query.includes('\n') ||
            query.startsWith(' ') ||
            query.endsWith(' ') ||
            !/^[\p{L}\p{N} .'-]*$/u.test(query)
        ) {
            return null;
        }
        return {
            startIndex: index,
            query,
        };
    }
    return null;
}
